import { isDockingModule } from "./sim/docking";
import { shipCargoBreakdown } from "./sim/ship";
import {
  resourcePricePerKg,
  shipCargoCapacityKg,
  shipCargoMassKg,
  transferResource,
  unionCargoResources,
} from "./sim/trade";
import type { DockMode, Ship, ShipModule, World } from "./sim/types";
import { clamp } from "./util/math";
import { makePanelHeaderRef } from "./util/panel";

interface RowRefs {
  resource: string;
  slider: HTMLInputElement;
  usStock: HTMLSpanElement;
  themStock: HTMLSpanElement;
  usArrow: HTMLSpanElement;
  themArrow: HTMLSpanElement;
}

export class TradePanel {
  private root: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private statusEl: HTMLDivElement;
  private applyBtn: HTMLButtonElement;
  private titleEl: HTMLDivElement;
  private capUsEl: HTMLSpanElement | null = null;
  private capThemEl: HTMLSpanElement | null = null;

  private host: Ship | null = null;
  private partner: Ship | null = null;
  private mode: DockMode = "trade";
  private rows: RowRefs[] = [];
  private pending = new Map<string, number>();

  constructor(private getWorld: () => World) {
    this.root = document.createElement("div");
    this.root.id = "trade-panel";
    this.root.className = "panel trade-panel";
    this.root.style.display = "none";

    const { header, title } = makePanelHeaderRef(() => this.hide());
    this.titleEl = title;

    this.bodyEl = document.createElement("div");
    this.bodyEl.className = "panel-body trade-body";

    const footer = document.createElement("div");
    footer.className = "trade-footer";
    this.statusEl = document.createElement("div");
    this.statusEl.className = "trade-status";
    this.applyBtn = document.createElement("button");
    this.applyBtn.className = "control-btn";
    this.applyBtn.textContent = "APPLY";
    this.applyBtn.addEventListener("click", () => this.executePending());
    footer.appendChild(this.statusEl);
    footer.appendChild(this.applyBtn);

    this.root.appendChild(header);
    this.root.appendChild(this.bodyEl);
    this.root.appendChild(footer);
    document.body.appendChild(this.root);
  }

  hide(): void {
    this.root.style.display = "none";
    this.host = null;
    this.partner = null;
    this.rows = [];
    this.pending.clear();
  }

  isVisible(): boolean {
    return this.root.style.display !== "none";
  }

  openForCollar(host: Ship, hostCollar: ShipModule): boolean {
    if (!isDockingModule(hostCollar)) return false;
    const dock = hostCollar.docking;
    if (!dock || dock.peerShipId == null) return false;
    const partner = this.getWorld().ships.get(dock.peerShipId);
    if (!partner) return false;
    this.host = host;
    this.partner = partner;
    this.mode = dock.mode;
    this.pending.clear();
    this.buildRows();
    this.updateStatus();
    this.root.style.display = "flex";
    return true;
  }

  refresh(): void {
    if (!this.isVisible() || !this.host || !this.partner) return;
    const world = this.getWorld();
    if (!world.ships.has(this.host.id) || !world.ships.has(this.partner.id)) {
      this.hide();
      return;
    }
    if (this.host.tradeWithShipId !== this.partner.id
        && this.host.boardedByShipId !== this.partner.id
        && this.partner.boardedByShipId !== this.host.id) {
      this.hide();
      return;
    }
    this.updateRowsText();
    this.updateStatus();
  }

  private buildRows(): void {
    const host = this.host!;
    const partner = this.partner!;
    this.titleEl.textContent =
      `${this.mode === "board" ? "BOARDED" : "TRADE"} — ${host.name} ↔ ${partner.name}`;
    this.bodyEl.innerHTML = "";
    this.rows = [];

    const resources = unionCargoResources(host, partner);

    const headRow = document.createElement("div");
    headRow.className = "trade-row trade-head";
    headRow.innerHTML = `
      <span class="trade-cell trade-us">${host.name} (kg)</span>
      <span class="trade-cell trade-arrow"></span>
      <span class="trade-cell trade-name">resource</span>
      <span class="trade-cell trade-slider"></span>
      <span class="trade-cell trade-arrow"></span>
      <span class="trade-cell trade-them">${partner.name} (kg)</span>
      <span class="trade-cell trade-price">${this.mode === "trade" ? "cr/kg" : ""}</span>
    `;
    this.bodyEl.appendChild(headRow);

    if (resources.length === 0) {
      const empty = document.createElement("div");
      empty.className = "trade-empty";
      empty.textContent = "neither ship is carrying any cargo";
      this.bodyEl.appendChild(empty);
    }
    for (const res of resources) this.addRow(res);

    const capRow = document.createElement("div");
    capRow.className = "trade-cap-row";
    const capUs = makeCell("trade-cap trade-cap-us");
    const label = makeCell("trade-cap-label");
    label.textContent = "CARGO";
    const capThem = makeCell("trade-cap trade-cap-them");
    capRow.append(capUs, label, capThem);
    this.bodyEl.appendChild(capRow);
    this.capUsEl = capUs;
    this.capThemEl = capThem;
    this.updateCapacity();
  }

  private addRow(resource: string): void {
    const row = document.createElement("div");
    row.className = "trade-row";
    row.dataset.resource = resource;

    const usStock = makeCell("trade-cell trade-us");
    const usArrow = makeCell("trade-cell trade-arrow");
    const name = makeCell("trade-cell trade-name");
    name.textContent = resource;

    const sliderCell = makeCell("trade-cell trade-slider");
    const slider = document.createElement("input");
    slider.type = "range";
    slider.step = "1";
    slider.value = "0";
    sliderCell.appendChild(slider);

    const themArrow = makeCell("trade-cell trade-arrow");
    const themStock = makeCell("trade-cell trade-them");

    const price = makeCell("trade-cell trade-price");
    const cr = resourcePricePerKg(resource);
    price.textContent = this.mode === "trade"
      ? (cr > 0 ? `${cr.toFixed(2)}` : "—")
      : "free";

    row.append(usStock, usArrow, name, sliderCell, themArrow, themStock, price);
    this.bodyEl.appendChild(row);

    const refs: RowRefs = { resource, slider, usStock, themStock, usArrow, themArrow };
    this.rows.push(refs);

    slider.addEventListener("input", () => {
      const v = Number(slider.value);
      if (v === 0) this.pending.delete(resource);
      else this.pending.set(resource, v);
      usArrow.textContent = v < 0 ? "←" : "";
      themArrow.textContent = v > 0 ? "→" : "";
      this.updateRowText(refs);
      this.updateCapacity();
      this.updateStatus();
    });

    this.updateRowText(refs);
  }

  private updateRowsText(): void {
    for (const r of this.rows) this.updateRowText(r);
    this.updateCapacity();
  }

  private updateCapacity(): void {
    const host = this.host;
    const partner = this.partner;
    if (!host || !partner || !this.capUsEl || !this.capThemEl) return;
    let net = 0;
    for (const kg of this.pending.values()) net += kg;
    const hostMass = shipCargoMassKg(host);
    const partnerMass = shipCargoMassKg(partner);
    const hostCap = shipCargoCapacityKg(host);
    const partnerCap = shipCargoCapacityKg(partner);
    fillCapCell(this.capUsEl, hostMass, -net, hostCap);
    fillCapCell(this.capThemEl, partnerMass, net, partnerCap);
  }

  private updateRowText(r: RowRefs): void {
    const host = this.host;
    const partner = this.partner;
    if (!host || !partner) return;
    const hostHave = shipCargoBreakdown(host).get(r.resource) ?? 0;
    const themHave = shipCargoBreakdown(partner).get(r.resource) ?? 0;

    const hostRoom = Math.max(0, shipCargoCapacityKg(host) - shipCargoMassKg(host));
    const themRoom = Math.max(0, shipCargoCapacityKg(partner) - shipCargoMassKg(partner));
    let sendCap = Math.min(hostHave, themRoom);
    let receiveCap = Math.min(themHave, hostRoom);
    if (this.mode === "trade") {
      const cr = resourcePricePerKg(r.resource);
      if (cr > 0) {
        sendCap = Math.min(sendCap, partner.credits / cr);
        receiveCap = Math.min(receiveCap, host.credits / cr);
      }
    }
    const newMax = Math.floor(sendCap);
    const newMin = -Math.floor(receiveCap);
    if (r.slider.min !== String(newMin)) r.slider.min = String(newMin);
    if (r.slider.max !== String(newMax)) r.slider.max = String(newMax);
    let delta = Number(r.slider.value);
    const clamped = clamp(delta, newMin, newMax);
    if (clamped !== delta) {
      r.slider.value = String(clamped);
      delta = clamped;
      if (delta === 0) this.pending.delete(r.resource);
      else this.pending.set(r.resource, delta);
    }

    const hostNext = hostHave - delta;
    const themNext = themHave + delta;
    r.usStock.textContent = `${hostNext.toFixed(0)}`;
    r.themStock.textContent = `${themNext.toFixed(0)}`;
    setDeltaClass(r.usStock, -delta);
    setDeltaClass(r.themStock, delta);
    r.usArrow.textContent = delta < 0 ? "←" : "";
    r.themArrow.textContent = delta > 0 ? "→" : "";
  }

  private updateStatus(): void {
    const host = this.host;
    const partner = this.partner;
    this.statusEl.innerHTML = "";
    if (!host || !partner) return;
    let netCost = 0;
    for (const [resource, kg] of this.pending) {
      if (kg === 0) continue;
      netCost += kg * resourcePricePerKg(resource);
    }
    if (this.mode === "trade") {
      const hostNext = host.credits + netCost;
      const partnerNext = partner.credits - netCost;
      this.statusEl.appendChild(makeCreditChip(host.name, hostNext, netCost));
      this.statusEl.appendChild(document.createTextNode("   |   "));
      this.statusEl.appendChild(makeCreditChip(partner.name, partnerNext, -netCost));
      this.applyBtn.disabled = this.pending.size === 0
        || netCost > partner.credits
        || netCost < -host.credits;
    } else {
      this.statusEl.textContent = "free transfer — capped by cargo capacity";
      this.applyBtn.disabled = this.pending.size === 0;
    }
    this.applyBtn.classList.toggle("disabled", this.applyBtn.disabled);
  }

  private executePending(): void {
    const host = this.host;
    const partner = this.partner;
    if (!host || !partner) return;
    for (const [resource, kg] of this.pending) {
      if (kg === 0) continue;
      if (kg > 0) transferResource(host, partner, resource, kg, this.mode);
      else        transferResource(partner, host, resource, -kg, this.mode);
    }
    this.pending.clear();
    for (const r of this.rows) {
      r.slider.value = "0";
      r.usArrow.textContent = "";
      r.themArrow.textContent = "";
    }
    this.updateRowsText();
    this.updateStatus();
  }
}

function setDeltaClass(el: HTMLElement, delta: number): void {
  el.classList.toggle("trade-gain", delta > 0);
  el.classList.toggle("trade-loss", delta < 0);
}

function makeCreditChip(name: string, projected: number, delta: number): HTMLSpanElement {
  const span = document.createElement("span");
  span.textContent = `${name}: ${Math.floor(projected)} cr`;
  setDeltaClass(span, delta);
  return span;
}

function makeCell(className: string): HTMLSpanElement {
  const el = document.createElement("span");
  el.className = className;
  return el;
}

function fillCapCell(
  el: HTMLSpanElement,
  currentKg: number,
  delta: number,
  capKg: number,
): void {
  el.innerHTML = "";
  el.appendChild(document.createTextNode(`${currentKg.toFixed(0)}`));
  if (delta !== 0) {
    const d = document.createElement("span");
    d.textContent = delta > 0 ? ` +${delta.toFixed(0)}` : ` ${delta.toFixed(0)}`;
    setDeltaClass(d, delta);
    el.appendChild(d);
  }
  el.appendChild(document.createTextNode(` / ${capKg.toFixed(0)} kg`));
}
